import { AlertCircle, Menu, UserX } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import NotificationBell from '@/components/notifications/NotificationBell';
import useInertiaData from '@/hooks/useInertiaData';

import HeaderSearch from './HeaderSearch';
import PlatformSelector from './PlatformSelector';
import UserDropdown from './UserDropdown';

const Header: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { is_impersonating, pending_billing_order, no_billing_order_record, account_paused } = useInertiaData();
    const { t } = useTranslation();
    const isImpersonating = Boolean(is_impersonating);

    let paymentState: 'ok' | 'warning' | 'urgent' = 'ok';
    if (pending_billing_order) {
        const updatedAt = new Date(pending_billing_order.updated_at);
        const daysSince = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
        paymentState = daysSince >= 3 ? 'urgent' : 'warning';
    }

    const showNoBillingOrderBanner = Boolean(
        no_billing_order_record && !window.location.pathname.startsWith('/no-billing-order')
    );

    // Function to open mobile menu
    const openMobileMenu = () => {
        setIsMobileMenuOpen(true);
        // Dispatch custom event to notify Sidebar
        window.dispatchEvent(new CustomEvent('openMobileMenu'));
    };

    return (
        <>
            <div className="sticky top-0 z-30 shadow-sm">
                {account_paused && (
                    <div className="border-b-2 border-gray-600 bg-gradient-to-r from-gray-700 to-gray-800 px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-white">
                            <AlertCircle size={18} />
                            <span>{t('accountPaused.message')}</span>
                            <a
                                href="/help/"
                                className="ml-4 rounded bg-white px-3 py-1 text-xs font-bold text-gray-700 hover:bg-gray-100"
                            >
                                {t('accountPaused.action')}
                            </a>
                        </div>
                    </div>
                )}
                {isImpersonating && (
                    <div className="border-b-2 border-orange-600 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-white">
                            <AlertCircle size={18} />
                            <span>
                                Impersonating User - You are viewing the site as
                                another user
                            </span>
                            <a
                                href="/impersonate/stop/"
                                className="ml-4 flex items-center gap-1 rounded bg-white px-3 py-1 text-xs font-bold text-orange-600 transition-colors hover:bg-orange-50"
                            >
                                <UserX size={14} />
                                Leave Impersonation
                            </a>
                        </div>
                    </div>
                )}
                {paymentState === 'warning' && (
                    <div className="border-b-2 border-yellow-500 bg-gradient-to-r from-yellow-400 to-amber-400 px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-yellow-900">
                            <AlertCircle size={18} />
                            <span>{t('paymentBanner.warningMessage')}</span>
                            <a
                                href="/my-account/?tab=billing"
                                className="ml-4 rounded bg-yellow-900 px-3 py-1 text-xs font-bold text-yellow-100 hover:bg-yellow-800"
                            >
                                {t('paymentBanner.warningAction')}
                            </a>
                        </div>
                    </div>
                )}
                {paymentState === 'urgent' && (
                    <div className="border-b-2 border-red-700 bg-gradient-to-r from-red-600 to-red-700 px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-white">
                            <AlertCircle size={18} />
                            <span>{t('paymentBanner.urgentMessage')}</span>
                            <a
                                href="/my-account/?tab=billing"
                                className="ml-4 rounded bg-white px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50"
                            >
                                {t('paymentBanner.urgentAction')}
                            </a>
                        </div>
                    </div>
                )}
                {showNoBillingOrderBanner && (
                    <div className="border-b-2 border-orange-400 bg-gradient-to-r from-orange-400 to-amber-500 px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-white">
                            <AlertCircle size={18} />
                            <span>{t('noBillingOrder.warningMessage')}</span>
                            <a
                                href="/no-billing-order/"
                                className="ml-4 rounded bg-white px-3 py-1 text-xs font-bold text-orange-600 hover:bg-orange-50"
                            >
                                {t('noBillingOrder.warningAction')}
                            </a>
                        </div>
                    </div>
                )}
                <header className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        {/* Mobile menu button */}
                        <button
                            onClick={openMobileMenu}
                            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 lg:hidden"
                        >
                            <Menu size={20} />
                        </button>

                        {/* Platform Selector */}
                        <PlatformSelector />

                        {/* Header Right Section */}
                        <div className="flex items-center space-x-4">
                            {/* Search Bar - Hidden on mobile */}
                            <div className="hidden lg:block">
                                <HeaderSearch />
                            </div>

                            {/* Notification Bell */}
                            <NotificationBell />

                            {/* User Profile Dropdown */}
                            <UserDropdown />
                        </div>
                    </div>
                </header>
            </div>
        </>
    );
};

export default Header;
